import cx from 'classnames'
import { useState } from 'react'
import { Modal } from 'almanac-ui/Modal'

import Styles from './Uploads.module.scss'
import ImageMenuModalStyles from '../ImageMenuModal.module.scss'
import ImagePickerModalUploadsStyles from 'components/ImagePickerModal/Uploads/Uploads.module.scss'
import ControlsStyles from '../Controls/Controls.module.scss'

import { useUploads } from 'components/Design/UploadsTab/useUploads'
import { collectResults } from 'utils/query'
import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Images3, Loader1, VideoOn } from 'blaze-ui/icons'
import { Pressable } from '@react-aria/interactions'
import { Text } from 'blaze-ui/Text/Text'
import { ScrollContainer } from 'components/ImagePickerModal/ScrollContainer/ScrollContainer'
import { Grid } from 'components/ImagePickerModal/Grid/Grid'
import { useImageMenuContext } from '../ImageMenuContext'
import { useDropzone } from 'react-dropzone'
import {
  ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES,
  ACCEPTED_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { UploadButton } from 'screens/blaze/Home/components/UploadButton/UploadButton'
import { DragOverlay } from 'screens/Uploads/Dropzone/Dropzone'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { ImageThumbnail } from 'components/ImagePickerModal/ImageThumbnail/ImageThumbnail'
import { getMediaFiles } from 'components/Design/UploadsTab/getMediaFiles'
import { Actions } from '../ImageMenuModal'
import { getAcceptedFiles } from 'components/ImageMenu/utils'

type Filter = 'all' | 'video' | 'image'

interface PillProps {
  icon: React.ReactNode
  text: string
  active: boolean
  onPress: () => void
}

const Pill = ({ icon, text, active, onPress }: PillProps) => {
  const color = active ? 'var(--light-100)' : 'var(--dark-80)'

  return (
    <Pressable onPress={() => onPress()}>
      <div className={cx(ControlsStyles.pill, { [ControlsStyles.active]: active })}>
        {icon}
        <Text variant="secondary" color={color}>
          {text}
        </Text>
      </div>
    </Pressable>
  )
}

const EmptyState = () => {
  const { setProvider } = useImageMenuContext()

  const { getRootProps, isDragActive } = useDropzone({
    accept: ACCEPTED_MIME_TYPES,
    onDropRejected: () => {},
    onDropAccepted: () => {},
    disabled: false,
  })

  return (
    <>
      <Modal.Header className={ImageMenuModalStyles.header} title="Upload Images or Videos" />
      <Modal.Content className={Styles.emptyContent}>
        <div className={Styles.emptyState} {...getRootProps()}>
          <div className={Styles.innerEmptyState}>
            <div className={Styles.top}>
              <img
                src="https://res.cloudinary.com/almanac/image/upload/v1744897958/blaze_assets/hd_icons/o3hfo5y4rutwqszg7f0q.png"
                alt=""
                width={161}
              />
            </div>
            <div className={Styles.middle}>
              <Heading level={2}>Drag and Drop or click "Choose Files"</Heading>
              <Text color="var(--dark-60)">One pagers, outbound emails, presentations, blog posts etc. </Text>
            </div>
            <div className={Styles.bottom}>
              <UploadButton size="md" label="Choose Files" />
              <Text variant="metadata" color="var(--dark-60)">
                Blaze supports JPG, JPEG, PNG, MPEG, MP4, PDF
              </Text>
            </div>
          </div>
          <DragOverlay isActive={isDragActive}>
            <img
              src="https://res.cloudinary.com/almanac/image/upload/v1746286434/blaze_assets/hd_icons/image_menu_dropzone_graphic_bpog0c.png"
              alt=""
              width={160}
            />
          </DragOverlay>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button size="lg" variant="tertiary" onPress={() => setProvider('')}>
            Back
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Actions />
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}

export const Uploads = () => {
  const { setProvider, selectedImages, toggleSelectedImage, accept } = useImageMenuContext()
  const [filter, setFilter] = useState<Filter>('all')
  const uploadsQuery = useUploads()
  const uploadFiles = collectResults(uploadsQuery, (data) => data.files)
  const uploads = getAcceptedFiles(uploadFiles, accept)
  const mediaFiles = getMediaFiles(uploads, ACCEPTED_MIME_TYPES)
  const filteredMediaFiles =
    filter === 'all' ? mediaFiles : mediaFiles.filter((file) => file.mime_type.includes(filter))

  if (!uploads.length) {
    return <EmptyState />
  }
  return (
    <>
      <Modal.Header className={ImageMenuModalStyles.header} title="Uploads" />
      <Modal.Content className={Styles.content}>
        {accept === ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES && (
          <div className={Styles.filters}>
            <Pill onPress={() => setFilter('all')} icon={<Loader1 />} text="All" active={filter === 'all'} />
            <Pill onPress={() => setFilter('video')} icon={<VideoOn />} text="Videos" active={filter === 'video'} />
            <Pill onPress={() => setFilter('image')} icon={<Images3 />} text="Images" active={filter === 'image'} />
          </div>
        )}
        <div className={Styles.scrollContainer}>
          <ScrollContainer>
            <Grid.Root breakpointCols={4}>
              {filteredMediaFiles.map((mediaFile, index) => (
                <div key={mediaFile.id} className={ImagePickerModalUploadsStyles.allUploadsItem}>
                  <ImageThumbnail
                    image={{ ...mediaFile, url: mediaFile.thumbnail_url as string }}
                    onToggleSelect={(image) => toggleSelectedImage({ ...image, provider: 'uploads' })}
                    isSelected={selectedImages.some((i) => i.url === mediaFile.thumbnail_url)}
                    duration={mediaFile.duration}
                  />
                </div>
              ))}
            </Grid.Root>
            {uploadsQuery.hasNextPage && (
              <div className={Styles.infiniteLoader}>
                <InfiniteScroll hasMore={uploadsQuery.hasNextPage} loadMore={uploadsQuery.fetchNextPage} />
              </div>
            )}
          </ScrollContainer>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button size="lg" variant="tertiary" onPress={() => setProvider('')}>
            Back
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Actions />
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
